import { Address } from "@certn/cascadia/patterns/address-form/types";
import { css } from "@emotion/css";
import { ExpandableCard } from "@certn/cascadia-react";
import { Skeleton } from "@certn/cascadia-react/patterns/skeleton/Skeleton";
import { Text } from "@certn/cascadia";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { useProfile } from "../../auth/ProfileProvider";
import { BillingAddressFormCore } from "./BillingAddressFormCore";

interface BillingAddressProps {
  // Address from applicant_form_data.residences[].address
  // Uses the shared Address type from cascadia (same as used in Address History/Residences controls)
  caseAddress?: Address | null;
  // Callback called when the billing address form save button enabled state changes
  onBillingFormSaveButtonEnabledChange?: (enabled: boolean) => void;
}

export function BillingAddress({
  caseAddress,
  onBillingFormSaveButtonEnabledChange,
}: BillingAddressProps = {}) {
  const { t } = useTranslation();
  const { profile } = useProfile();
  // Billing address from profile (source of truth if it exists)
  const billingAddress = profile?.billing_address;

  // Priority logic for payment summary page:
  // 1. If billing address exists: Show it (source of truth)
  // 2. If billing address doesn't exist: Pre-populate form with case address
  const currentAddress = billingAddress || caseAddress || undefined;

  const [isExpanded, setIsExpanded] = useState(!currentAddress?.street_address);

  const onToggle = useCallback(() => setIsExpanded((prev) => !prev), []);

  const handleSave = useCallback(() => {
    onToggle();
  }, [onToggle]);

  if (!profile) {
    return (
      <ExpandableCard
        title={t("Billing address")}
        subtitle={<Skeleton count={3} width={120} />}
      />
    );
  }

  // Render subtitle: if billing address exists, show its details; otherwise show "Same as Case address"
  const renderSubtitle = () => {
    if (isExpanded) return null;

    if (billingAddress) {
      // Billing address exists - show its details
      const { street_address, locality, postal_code } = billingAddress;
      return (
        <Text style="form/body-secondary" color="text-weak">
          {street_address}
          <br />
          {locality}
          <br />
          {postal_code}
        </Text>
      );
    }

    // No billing address - using case address fallback
    return <Text>{t("Same as case address")}</Text>;
  };

  return (
    <ExpandableCard
      expanded={isExpanded}
      onToggle={onToggle}
      title={t("Billing address")}
      iconLabel={
        currentAddress?.street_address && !isExpanded ? t("Edit") : undefined
      }
      subtitle={renderSubtitle()}
    >
      {isExpanded && (
        <div className={styles.form}>
          <BillingAddressFormCore
            defaultAddress={caseAddress ?? undefined}
            checkboxLabel={t("Use case address")}
            initialBillingAddress={billingAddress ?? undefined}
            onSave={handleSave}
            testID="billing_address"
            submitButtonLabel={t("Save billing address")}
            onSaveButtonEnabledChange={onBillingFormSaveButtonEnabledChange}
          />
        </div>
      )}
    </ExpandableCard>
  );
}

const styles = {
  form: css({
    paddingLeft: "var(--space-4)",
    paddingRight: "var(--space-4)",
    paddingTop: "var(--space-2)",
    paddingBottom: "var(--space-2)",
  }),
};
